import type {
  BioData,
  CityLink,
  Decision,
  Qualification,
  RubricScores,
  ScoredResult,
} from '../types/onboarding'
import { tnsCityLinks } from '../data/tnsCityLinks'

/** Existing Apps Script web app that owns the sheet write. */
export const TNS_SUBMIT_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbzR_-KmqKZFGGbKzHfTDJ1Shbo07C0RSOwx4RKTH9sU4PjA-r7elviN739j-03EfNIa/exec'

/**
 * Magic Patterns' server-side AI proxy. The API key is injected by the proxy,
 * so no credential is ever present in client code.
 */
const AI_PROXY_ENDPOINT = 'https://www.requests.magicpatterns.com/openai/proxy'

/* ------------------------------------------------------------------ *
 * City Links — read on load
 * ------------------------------------------------------------------ */

const normalizeBool = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    return v === 'true' || v === 'yes' || v === '1' || v === 'active'
  }
  return false
}

const coerceCityRows = (rows: unknown): CityLink[] | null => {
  if (!Array.isArray(rows)) return null
  const mapped = rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null
      const r = row as Record<string, unknown>
      const cityId = String(r.city_id ?? r.cityId ?? '').trim()
      const cityName = String(r.city_name ?? r.cityName ?? '').trim()
      if (!cityId || !cityName) return null
      return {
        city_id: cityId,
        city_name: cityName,
        whatsapp_link: String(
          r.whatsapp_link ?? r.whatsappLink ?? '',
        ).trim(),
        active: normalizeBool(r.active),
      } as CityLink
    })
    .filter((r): r is CityLink => r !== null)
  return mapped.length > 0 ? mapped : null
}

/**
 * Reads the "City Links" tab. Attempts the live read first, then falls back to
 * the seeded rows in data/tnsCityLinks.ts so the dropdown is never empty.
 * Only rows where `active` is true are returned.
 */
export const fetchCityLinks = async (): Promise<CityLink[]> => {
  try {
    const res = await fetch(
      `${TNS_SUBMIT_ENDPOINT}?action=cityLinks&tab=${encodeURIComponent('City Links')}`,
      { method: 'GET', redirect: 'follow' },
    )
    if (res.ok) {
      const data = await res.json()
      const rows = coerceCityRows(
        Array.isArray(data) ? data : (data?.rows ?? data?.data ?? data?.cities),
      )
      if (rows) return rows.filter((c) => c.active)
    }
  } catch {
    // Live read unavailable — fall through to the seeded rows.
  }
  return tnsCityLinks.filter((c) => c.active)
}

export const findCityById = (
  cities: CityLink[],
  cityId: string,
): CityLink | undefined => cities.find((c) => c.city_id === cityId)

/* ------------------------------------------------------------------ *
 * Scoring
 * ------------------------------------------------------------------ */

const RUBRIC_SYSTEM_PROMPT = `You are evaluating applications to a high-trust professional membership community.

Score each answer strictly on the evidence it contains. Do not reward eloquence, ambition, or good intentions — reward specificity and demonstrated outcomes.

Scale for q1 and q2 (0-3):
0 = vague, generic, or no concrete example given
1 = names an intention or a general pattern of behaviour, but no specific instance
2 = describes a specific action the applicant took, but the outcome is unclear or modest
3 = describes a specific action with a clear, tangible outcome for another person

Scale for q4 (0-2). This answer is forward-looking rather than evidence-based, so it carries less weight:
0 = vague or nothing concrete offered
1 = names a general capability or willingness to contribute
2 = names a specific, concrete contribution they are positioned to make

Respond with ONLY a JSON object in exactly this shape, with integer values:
{"q1": <0-3>, "q2": <0-3>, "q4": <0-2>}`

/** Deterministic fallback used only if the AI call fails, so the flow never dead-ends. */
const heuristicScores = (
  q1: string,
  q2: string,
  q4: string,
): RubricScores => {
  const grade = (text: string, max: number): number => {
    const trimmed = text.trim()
    const words = trimmed.split(/\s+/).filter(Boolean).length
    // Signals of a specific, evidenced answer.
    const hasNumbers = /\d/.test(trimmed)
    const hasOutcome =
      /\b(result|resulted|led to|landed|raised|hired|grew|launched|secured|increased|closed|got|became|now)\b/i.test(
        trimmed,
      )
    let score = 0
    if (words >= 15) score = 1
    if (words >= 35 && (hasNumbers || hasOutcome)) score = 2
    if (words >= 60 && hasNumbers && hasOutcome) score = 3
    return Math.min(score, max)
  }
  return {
    q1: grade(q1, 3),
    q2: grade(q2, 3),
    q4: grade(q4, 2),
  }
}

const clamp = (n: unknown, max: number): number => {
  const parsed = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(parsed)) return 0
  return Math.max(0, Math.min(max, Math.round(parsed)))
}

const requestRubricScores = async (
  q1: string,
  q2: string,
  q4: string,
): Promise<RubricScores> => {
  const res = await fetch(AI_PROXY_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'gpt-4o',
      temperature: 0,
      max_tokens: 120,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: RUBRIC_SYSTEM_PROMPT },
        {
          role: 'user',
          content: JSON.stringify({
            q1: `Tell us about a specific time you used your resources, skill, network, or money to help someone else advance. ANSWER: ${q1}`,
            q2: `Where have you shaped culture in a community, workplace, or industry? ANSWER: ${q2}`,
            q4: `Beyond your presence, what would you personally bring into this room? ANSWER: ${q4}`,
          }),
        },
      ],
    }),
  })
  if (!res.ok) throw new Error(`Scoring request failed: ${res.status}`)
  const payload = await res.json()
  const raw = payload?.choices?.[0]?.message?.content
  if (!raw) throw new Error('Scoring response was empty')
  const parsed = JSON.parse(raw)
  return {
    q1: clamp(parsed.q1, 3),
    q2: clamp(parsed.q2, 3),
    q4: clamp(parsed.q4, 2),
  }
}

const decisionFromScore = (score: number): Decision => {
  if (score >= 10) return 'approved'
  if (score >= 6) return 'review'
  return 'declined'
}

/**
 * Scores an application and returns the final decision.
 *
 * Hard filter: a "No" on financial commitment is an immediate decline and the
 * AI call is skipped entirely.
 *
 * Otherwise: AI Score = q1 + q2 + q4 + consistencyRating (max 13).
 */
export const scoreApplication = async (
  qualification: Qualification,
): Promise<ScoredResult> => {
  if (qualification.financialCommitment === 'No') {
    return { aiScore: 0, decision: 'declined' }
  }

  const consistency = qualification.consistencyRating ?? 0
  let scores: RubricScores

  try {
    scores = await requestRubricScores(
      qualification.q1HelpedSomeone,
      qualification.q2ShapedCulture,
      qualification.q4BringToRoom,
    )
  } catch {
    scores = heuristicScores(
      qualification.q1HelpedSomeone,
      qualification.q2ShapedCulture,
      qualification.q4BringToRoom,
    )
  }

  const aiScore = scores.q1 + scores.q2 + scores.q4 + consistency
  return { aiScore, decision: decisionFromScore(aiScore) }
}

/* ------------------------------------------------------------------ *
 * Single write — on final submit only
 * ------------------------------------------------------------------ */

export const submitOnboarding = async (params: {
  bio: BioData
  qualification: Qualification
  cities: CityLink[]
  aiScore: number
  decision: Decision
}): Promise<void> => {
  const { bio, qualification, cities, aiScore, decision } = params
  const city = findCityById(cities, bio.cityId)
  const resolvedCityName =
    bio.cityId === 'OTH' && bio.otherCityName.trim()
      ? bio.otherCityName.trim()
      : (city?.city_name ?? '')

  const payload: Record<string, string> = {
    Timestamp: new Date().toISOString(),
    Type: 'onboarding',
    'Full Name': bio.fullName.trim(),
    Email: bio.email.trim(),
    Phone: bio.phone.trim(),
    'Brand Name': bio.brandName.trim(),
    Expertise: bio.expertise.trim(),
    'City ID': bio.cityId,
    'City Name': resolvedCityName,
    'Q1 Helped Someone': qualification.q1HelpedSomeone.trim(),
    'Q2 Shaped Culture': qualification.q2ShapedCulture.trim(),
    'Q3 Building Now': qualification.q3BuildingNow.trim(),
    'Q4 Bring To Room': qualification.q4BringToRoom.trim(),
    'Consistency Rating': String(qualification.consistencyRating ?? ''),
    'Financial Commitment': qualification.financialCommitment,
    'AI Score': String(aiScore),
    Decision: decision,
  }

  const body = new URLSearchParams(payload)

  // Apps Script web apps are opaque to the browser: no readable response body.
  // Fire-and-forget, but awaited so we don't route to an outcome screen before
  // the request has actually left.
  await fetch(TNS_SUBMIT_ENDPOINT, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  })
}
